"use client";

import { SlidersHorizontal, X } from "lucide-react";
import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import type {
  ContentTypeFilter,
  ExploreBriefCard,
  ExploreStoryCard,
  FilterOption,
} from "../types";
import { SearchableTopicPills } from "./searchable-topic-pills";
import { TYPE_OPTIONS, useFilterFacets } from "./use-filter-facets";

// ─── Props ───────────────────────────────────────────────────

interface MobileFilterSheetProps {
  storiesForCategoryFacets: ExploreStoryCard[];
  briefsForCategoryFacets?: Array<{ categorySlug: string | null }>;
  storiesForTagFacets: ExploreStoryCard[];
  briefsForTagFacets?: ExploreBriefCard[];
  toolOptions: FilterOption[];
  tagDisplayNames: ReadonlyMap<string, string>;
  categoryDisplayNames: ReadonlyMap<string, string>;
  categoryEmojiBySlug: ReadonlyMap<string, string>;

  contentType: ContentTypeFilter;
  selectedCategorySlugs: string[];
  selectedTagSlugs: string[];
  selectedToolSlugs: string[];
  hasAnyFilter: boolean;
  activeFilterCount: number;

  onContentTypeChange: (value: ContentTypeFilter) => void;
  onCategoryToggle: ({ slug }: { slug: string }) => void;
  onTagToggle: ({ slug }: { slug: string }) => void;
  onToolToggle: ({ slug }: { slug: string }) => void;
  onResetAll: () => void;
}

// ─── Component ───────────────────────────────────────────────

export function MobileFilterSheet({
  storiesForCategoryFacets,
  briefsForCategoryFacets,
  storiesForTagFacets,
  briefsForTagFacets,
  toolOptions,
  tagDisplayNames,
  categoryDisplayNames,
  categoryEmojiBySlug,
  contentType,
  selectedCategorySlugs,
  selectedTagSlugs,
  selectedToolSlugs,
  hasAnyFilter,
  activeFilterCount,
  onContentTypeChange,
  onCategoryToggle,
  onTagToggle,
  onToolToggle,
  onResetAll,
}: MobileFilterSheetProps) {
  const [isOpen, setIsOpen] = useState(false);

  const selectedCategorySet = useMemo(
    () => new Set(selectedCategorySlugs),
    [selectedCategorySlugs]
  );
  const selectedTagSet = useMemo(
    () => new Set(selectedTagSlugs),
    [selectedTagSlugs]
  );
  const selectedToolSet = useMemo(
    () => new Set(selectedToolSlugs),
    [selectedToolSlugs]
  );

  const { categoryFacets, topicFacets } = useFilterFacets({
    storiesForCategoryFacets,
    briefsForCategoryFacets,
    storiesForTagFacets,
    briefsForTagFacets,
    toolOptions,
    tagDisplayNames,
    categoryDisplayNames,
    categoryEmojiBySlug,
  });
  const topicItems = useMemo(
    () =>
      topicFacets.map((topic) => ({
        ...topic,
        selected:
          topic.kind === "tool"
            ? selectedToolSet.has(topic.id)
            : selectedTagSet.has(topic.id),
        onSelect: () =>
          topic.kind === "tool"
            ? onToolToggle({ slug: topic.id })
            : onTagToggle({ slug: topic.id }),
      })),
    [onTagToggle, onToolToggle, selectedTagSet, selectedToolSet, topicFacets]
  );

  return (
    <div
      className="lg:hidden"
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
    >
      {/* ── Trigger button ── */}
      <button
        className="flex h-8 items-center gap-1 rounded-full border-0 bg-[var(--explore-tag-bg)] px-2.5 py-1.5 font-medium text-[0.7rem] text-[var(--explore-tag-text)] transition-all active:scale-[0.97]"
        onClick={() => setIsOpen(true)}
        type="button"
      >
        <SlidersHorizontal className="h-3 w-3" />
        Filters
        {activeFilterCount > 0 && (
          <span className="flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-[var(--explore-text-primary)] px-1 text-[0.65rem] text-[var(--explore-bg)]">
            {activeFilterCount}
          </span>
        )}
      </button>

      {/* ── Overlay ── */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[150] bg-[var(--explore-overlay-bg)] transition-opacity"
          onClick={() => setIsOpen(false)}
          role="presentation"
        />
      )}

      {/* ── Bottom sheet ── */}
      <div
        className={cn(
          "fixed right-0 bottom-0 left-0 z-[151] flex max-h-[85vh] flex-col rounded-t-[20px] bg-[var(--explore-bg-sidebar)] transition-transform duration-400",
          isOpen ? "translate-y-0" : "translate-y-full"
        )}
        style={{
          boxShadow: "var(--explore-shadow-sheet)",
          transitionTimingFunction: "cubic-bezier(0.32, 0.72, 0, 1)",
        }}
      >
        {/* Sticky header */}
        <div className="shrink-0 rounded-t-[20px] bg-[var(--explore-bg-sidebar)] px-5 pt-0 pb-4">
          {/* Drag handle */}
          <div className="mx-auto mt-2.5 mb-4 h-1 w-9 rounded-full bg-[var(--explore-border)]" />

          {/* Header row */}
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-[1.1rem] text-[var(--explore-text-primary)]">
              Filters
            </h3>
            <div className="flex items-center gap-3">
              {hasAnyFilter && (
                <button
                  className="border-none bg-transparent p-0 font-medium text-[0.8rem] text-[var(--explore-accent-teal)]"
                  onClick={() => {
                    onResetAll();
                  }}
                  type="button"
                >
                  Clear all
                </button>
              )}
              <button
                className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--explore-border-light)]"
                onClick={() => setIsOpen(false)}
                type="button"
              >
                <X className="h-4 w-4 text-[var(--explore-text-secondary)]" />
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto px-5 pb-8">
          {/* ── Type ── */}
          <SheetSection title="Type">
            <div className="flex flex-wrap gap-2">
              {TYPE_OPTIONS.map(({ value, label }) => (
                <MobileChip
                  key={value}
                  label={label}
                  onToggle={() =>
                    onContentTypeChange(contentType === value ? "all" : value)
                  }
                  selected={contentType === value}
                />
              ))}
            </div>
          </SheetSection>

          {/* ── Category ── */}
          {categoryFacets.length > 0 && (
            <SheetSection title="Category">
              <div className="flex flex-wrap gap-2">
                {categoryFacets.map((cat) => (
                  <button
                    className={cn(
                      "flex items-center gap-1.5 rounded-full border-0 px-3 py-1.5 font-medium text-[0.8rem] transition-all active:scale-[0.97]",
                      selectedCategorySet.has(cat.id)
                        ? "bg-[var(--explore-chip-selected-bg)] text-[var(--explore-chip-selected-text)]"
                        : "bg-[var(--explore-tag-bg)] text-[var(--explore-tag-text)]"
                    )}
                    key={cat.id}
                    onClick={() => onCategoryToggle({ slug: cat.id })}
                    type="button"
                  >
                    {cat.emoji && (
                      <span className="text-[0.85rem]">{cat.emoji}</span>
                    )}
                    {cat.label}
                    <span className="text-[0.68rem] opacity-60">
                      {cat.count}
                    </span>
                  </button>
                ))}
              </div>
            </SheetSection>
          )}

          {/* ── Tags + tools ── */}
          <SheetSection title="Tags">
            <SearchableTopicPills
              emptyState="No tags or tools are visible for the current filters."
              items={topicItems}
            />
          </SheetSection>
        </div>

        {/* Sticky footer */}
        <div className="shrink-0 border-[var(--explore-border-light)] border-t bg-[var(--explore-bg-sidebar)] px-5 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <button
            className="w-full rounded-xl border-none bg-[var(--explore-text-primary)] py-3.5 font-semibold text-[0.9rem] text-[var(--explore-bg)] transition-opacity active:opacity-80"
            onClick={() => setIsOpen(false)}
            type="button"
          >
            Show results
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────

function SheetSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-5">
      <p className="mb-2.5 font-semibold text-[0.7rem] text-[var(--explore-text-muted)] uppercase tracking-[0.1em]">
        {title}
      </p>
      {children}
    </div>
  );
}

function MobileChip({
  label,
  selected,
  onToggle,
}: {
  label: string;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      className={cn(
        "flex items-center gap-1 rounded-sm border-0 px-2.5 py-1.5 font-medium text-[0.8rem] transition-all active:scale-[0.97]",
        selected
          ? "bg-[var(--explore-chip-selected-bg)] text-[var(--explore-chip-selected-text)]"
          : "bg-[var(--explore-tag-bg)] text-[var(--explore-tag-text)]"
      )}
      onClick={onToggle}
      type="button"
    >
      {label}
    </button>
  );
}
